import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { formatAgo, formatDateTime, formatInt } from "@/lib/format";
import { hasOp } from "@/lib/stubs";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";

/** One import_runs row (crm-integrations). `stats` is only on imports_status / imports_run_get. */
interface ImportRun {
  id: number;
  kind: string;
  steps: string[];
  status: "running" | "ok" | "failed" | "cancelled" | string;
  started_at: string;
  finished_at: string | null;
  started_by: string;
  error: string | null;
  stats?: { counts?: Record<string, unknown>; notes?: unknown; took?: string };
}

interface ImportStatus {
  last_import: ImportRun | null;
  last_mirror: ImportRun | null;
  last_check: ImportRun | null;
  mirror_enabled: boolean;
  mirror_key_set: boolean;
  cursors: Record<string, string>;
  keap_calls_left_today: number | null;
  imported_current_contacts: number;
  imported_archived_contacts: number;
  placeholder_contacts: number;
  imported_keap_emails: number;
  history_finalized?: boolean;
}

const KIND: Record<string, string> = {
  keap_import: "Import from the Keap export",
  keap_history: "Keap email history",
  keap_credentials: "Site passwords (field 98)",
  keap_files: "Keap file attachments",
  keap_mirror: "Catch-up from Keap",
  keap_check: "Nightly count check",
};

const CHECK_LABEL: Record<string, string> = {
  contacts: "Contacts",
  notes: "Notes",
  orders: "Orders",
  invoices: "Invoices",
  subscriptions: "Subscriptions",
  active_subscriptions: "Active subscriptions",
  affiliates: "Partners (affiliates)",
  tags: "Tags",
};

function RunStatus({ r }: { r: ImportRun }) {
  if (r.status === "ok") return <Badge variant="success">finished</Badge>;
  if (r.status === "running") return <Badge variant="default">running</Badge>;
  if (r.status === "failed") return <Badge variant="destructive">failed</Badge>;
  return <Badge variant="muted">{r.status}</Badge>;
}

function took(r: ImportRun) {
  if (r.stats?.took) return r.stats.took.replace(/(\d+)h/, "$1 h ").replace(/(\d+)m/, "$1 min ").replace(/(\d+)s/, "$1 s").trim();
  if (!r.finished_at) return "";
  const s = Math.round((new Date(r.finished_at).getTime() - new Date(r.started_at).getTime()) / 1000);
  return s < 90 ? `${s} s` : `${Math.round(s / 60)} min`;
}

/** The nightly check's Keap-vs-CRM counts: counts["keap.contacts"] / counts["crm.contacts"]. */
function checkRows(r: ImportRun | null) {
  const c = r?.stats?.counts ?? {};
  const names = Object.keys(c)
    .filter((k) => k.startsWith("keap."))
    .map((k) => k.slice(5));
  const order = Object.keys(CHECK_LABEL);
  const rank = (n: string) => (order.indexOf(n) === -1 ? order.length : order.indexOf(n));
  return names
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((n) => ({ name: n, keap: Number(c[`keap.${n}`] ?? 0), crm: Number(c[`crm.${n}`] ?? 0) }));
}

function notesOf(r: ImportRun | null): string[] {
  const n = r?.stats?.notes;
  if (!n) return [];
  if (Array.isArray(n)) return n.map(String);
  if (typeof n === "object") return Object.entries(n as Record<string, unknown[]>).flatMap(([step, list]) => (Array.isArray(list) ? list.map((x) => `${step}: ${String(x)}`) : []));
  return [];
}

/** Settings → Keap import: where the one-time import and the Keap → CRM mirror stand (read-only, plus "catch up now"). */
export function KeapImportPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const available = useQuery({ queryKey: ["has-op", "imports_status"], queryFn: () => hasOp("imports_status") });
  const status = useQuery({
    queryKey: ["imports-status"],
    queryFn: () => api<ImportStatus>("/api/v1/imports/status"),
    enabled: available.data === true,
    refetchInterval: 30_000,
  });
  const runs = useQuery({
    queryKey: ["imports-runs"],
    queryFn: () => api<{ runs: ImportRun[] }>("/api/v1/imports/runs" + qs({ limit: 15 })),
    enabled: available.data === true,
    refetchInterval: 30_000,
  });
  const [confirmRun, setConfirmRun] = useState<"" | "catchup" | "check">("");
  const run = useMutation({
    mutationFn: (nightly: boolean) => api<{ queued: boolean; message: string }>("/api/v1/keap/mirror/run", { json: nightly ? { nightly: true } : {} }),
    onSuccess: (r) => {
      toast(r.message || "Queued. It shows up under Recent runs when it starts.", r.queued === false ? "error" : undefined);
      setConfirmRun("");
      void qc.invalidateQueries({ queryKey: ["imports-runs"] });
      void qc.invalidateQueries({ queryKey: ["imports-status"] });
    },
    onError: (e) => toast(e.message, "error"),
  });

  if (available.data === false) return <Empty title="The Keap import module isn't on this server yet." />;
  const s = status.data;
  if (!s) return <Skeleton className="h-48" />;
  const check = checkRows(s.last_check);
  const checkNotes = notesOf(s.last_check);
  const cursorTimes = Object.values(s.cursors)
    .map((v) => new Date(v))
    .filter((d) => !Number.isNaN(d.getTime()));
  const readUpTo = cursorTimes.length ? new Date(Math.min(...cursorTimes.map((d) => d.getTime()))).toISOString() : null;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Keap → CRM mirror</CardTitle>
            <Badge variant={s.mirror_enabled ? "success" : "muted"}>{s.mirror_enabled ? "on" : "off"}</Badge>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-[var(--color-muted-foreground)]">
              Until go-live, the CRM reads what changed in Keap every 15 minutes (tags, notes, opt-outs, orders, payments, site passwords) and compares totals every night. It only reads Keap;
              nothing is written back.
            </p>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1">
              <dt className="text-[var(--color-muted-foreground)]">Switch</dt>
              <dd>
                {s.mirror_enabled ? "On" : "Off"}
                <span className="text-xs text-[var(--color-muted-foreground)]"> (CRM settings: keap.mirror_enabled; ask Brandon before turning it on in production)</span>
              </dd>
              <dt className="text-[var(--color-muted-foreground)]">Keap key</dt>
              <dd>{s.mirror_key_set ? "Set" : <span className="text-[var(--color-warning-fg)]">Not set on this server</span>}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Read Keap up to</dt>
              <dd className="num">{readUpTo ? formatDateTime(readUpTo) : "Not started"}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Keap calls left today</dt>
              <dd className="num">{s.keap_calls_left_today == null ? "Unknown" : formatInt(s.keap_calls_left_today)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Last catch-up</dt>
              <dd>{s.last_mirror ? <LastRun r={s.last_mirror} /> : "None yet"}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Last nightly check</dt>
              <dd>{s.last_check ? <LastRun r={s.last_check} /> : "None yet"}</dd>
            </dl>
            {s.mirror_enabled && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => setConfirmRun("catchup")}>
                  Catch up now
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmRun("check")}>
                  Compare counts now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>What's been imported</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1">
              <dt className="text-[var(--color-muted-foreground)]">Current Keap contacts</dt>
              <dd className="num text-right">{formatInt(s.imported_current_contacts)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Archived contacts (deleted from Keap Sept 28-29; never emailed)</dt>
              <dd className="num text-right">{formatInt(s.imported_archived_contacts)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Placeholders (old payments whose contact Keap no longer has)</dt>
              <dd className="num text-right">{formatInt(s.placeholder_contacts)}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Keap emails in the timelines</dt>
              <dd className="num text-right">
                {formatInt(s.imported_keap_emails)}
                {s.history_finalized === false && <span className="block text-xs text-[var(--color-muted-foreground)]">history pull still running</span>}
              </dd>
            </dl>
            <div className="border-t pt-2">
              <span className="text-[var(--color-muted-foreground)]">Last import: </span>
              {s.last_import ? <LastRun r={s.last_import} /> : "None yet"}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>Keap and the CRM, side by side</CardTitle>
          {s.last_check && <span className="text-xs text-[var(--color-muted-foreground)]">nightly check, {formatDateTime(s.last_check.started_at)}</span>}
        </CardHeader>
        {check.length === 0 ? (
          <Empty title="No count check yet.">The first one runs the night after the mirror is switched on.</Empty>
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>What</TH>
                  <TH className="text-right">Keap</TH>
                  <TH className="text-right">CRM</TH>
                  <TH className="text-right">Difference</TH>
                </tr>
              </THead>
              <tbody>
                {check.map((c) => {
                  const diff = c.crm - c.keap;
                  return (
                    <TR key={c.name}>
                      <TD>{CHECK_LABEL[c.name] ?? c.name.replace(/_/g, " ")}</TD>
                      <TD className="num text-right">{formatInt(c.keap)}</TD>
                      <TD className="num text-right">{formatInt(c.crm)}</TD>
                      <TD className="num text-right">{diff === 0 ? <Badge variant="success">match</Badge> : <Badge variant="warning">{diff > 0 ? `+${formatInt(diff)}` : formatInt(diff)}</Badge>}</TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
            {checkNotes.length > 0 && (
              <ul className="space-y-0.5 border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
                {checkNotes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        {runs.isLoading && <Skeleton className="m-3 h-24" />}
        {runs.data && runs.data.runs.length === 0 && <Empty title="Nothing has run yet." />}
        {runs.data && runs.data.runs.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Started (MT)</TH>
                <TH>What</TH>
                <TH>Result</TH>
                <TH>Took</TH>
                <TH>Started by</TH>
              </tr>
            </THead>
            <tbody>
              {runs.data.runs.map((r) => (
                <TR key={r.id}>
                  <TD className="num whitespace-nowrap">{formatDateTime(r.started_at)}</TD>
                  <TD>
                    {KIND[r.kind] ?? r.kind.replace(/_/g, " ")}
                    {r.kind === "keap_import" && r.steps.length > 0 && <div className="max-w-[28rem] text-xs text-[var(--color-muted-foreground)]">{r.steps.join(", ")}</div>}
                    {r.error && <div className="max-w-[28rem] text-xs text-[var(--color-danger-fg)]">{r.error}</div>}
                  </TD>
                  <TD>
                    <RunStatus r={r} />
                  </TD>
                  <TD className="num whitespace-nowrap text-xs">{took(r)}</TD>
                  <TD className="text-xs">{r.started_by}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Dialog open={!!confirmRun} onOpenChange={(o) => !o && setConfirmRun("")}>
        <DialogContent className="w-[min(480px,calc(100vw-2rem))]">
          <DialogTitle>{confirmRun === "check" ? "Compare counts with Keap now?" : "Catch up from Keap now?"}</DialogTitle>
          <DialogDescription>
            {confirmRun === "check"
              ? "Counts contacts, notes, orders, invoices, subscriptions, partners and tags in Keap and here (about a dozen Keap calls). If the CRM has more contacts than Keap, it also archives the ones deleted in Keap (about 85 more calls). It never writes to Keap."
              : "Reads what changed in Keap since the last catch-up instead of waiting up to 15 minutes. It only reads Keap and uses a few Keap calls."}
          </DialogDescription>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmRun("")}>
              Cancel
            </Button>
            <Button disabled={run.isPending} onClick={() => run.mutate(confirmRun === "check")}>
              {confirmRun === "check" ? "Compare now" : "Catch up now"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LastRun({ r }: { r: ImportRun }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <RunStatus r={r} />
      <span title={formatDateTime(r.started_at)}>{formatAgo(r.started_at)}</span>
      {took(r) && <span className="text-xs text-[var(--color-muted-foreground)]">took {took(r)}</span>}
      {r.error && <span className="text-xs text-[var(--color-danger-fg)]">{r.error}</span>}
    </span>
  );
}
